import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { format } from "date-fns";
import CallDetailFilters from "./CallDetailFilters";
import CallDetailList from "./CallDetailList";

import * as codeActions from "../../../actions/codeActions";
import * as reportActions from "../../../actions/reportActions";
import * as exportActions from "../../../actions/exportActions";

import { customToast as toast } from "../../../utils";

import { downloadCSV, escapeCSVString } from "../utils";

import { formatDate } from "../../../helpers/date";

import { CALL_DETAIL_COLUMNS } from "./columns";

const CallDetailPage = ({ codes, actions, exporting, exported }) => {
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    page: 0,
    pageSize: 50,
    filtered: {
      startDate: format(new Date(new Date().getFullYear(), 0, 1), "yyyy-MM-dd"),
      endDate: format(new Date(), "yyyy-MM-dd")
    }
  });
  const [data, setData] = useState([]);
  const [count, setCount] = useState(0);
  const [exportingCSV, setExportingCSV] = useState(false);

  useEffect(() => {
    if (!codes.length) {
      actions.loadCodes();
    }
  }, [actions, codes]);

  useEffect(() => {
    if (exported) {
      const url = window.URL.createObjectURL(
        new Blob([exported], { type: "application/pdf" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = `Call-Detail-${new Date().getTime()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      actions.clearExported();
    }
  }, [exported, actions]);

  const fetchData = async (
    pageSize = filters.pageSize,
    page = 0,
    filtered = filters.filtered
  ) => {
    if (filters.filtered.startDate && filters.filtered.endDate) {
      setLoading(true);
      try {
        const report = await actions.getCallDetailReport({
          filtered,
          page,
          pageSize
        });
        if (report) {
          setData(report.calls);
          setCount(report.count);
        }
      } catch (error) {
        toast.error(
          "Request too large. Please adjust filters or contact support."
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const handleChange = (name) => (value) => {
    setLoading(false);
    if (
      Array.isArray(value) &&
      (name === "project" ||
        name === "department" ||
        name === "customer" ||
        name === "stage")
    ) {
      setFilters((prev) => ({
        ...prev,
        filtered: {
          ...prev.filtered,
          [name]: value.map((v) => ({ label: v.label, value: v.value }))
        }
      }));
    } else {
      setFilters((prev) => ({
        ...prev,
        filtered: { ...prev.filtered, [name]: value }
      }));
    }
  };

  const handlePageSizeChange = (pageSize) => {
    localStorage.setItem("DEFAULT_PAGE_SIZE_CALL_LIST", pageSize);
    setFilters((prev) => ({ ...prev, pageSize }));
    fetchData(
      parseInt(localStorage.getItem("DEFAULT_PAGE_SIZE_CALL_LIST")),
      filters.page
    );
  };

  const handlePageChange = async (page) => {
    setFilters((prev) => ({ ...prev, page }));
    fetchData(filters.pageSize, page);
  };

  const handleResetClick = () => {
    setFilters({
      page: 0,
      pageSize: 50,
      filtered: {
        startDate: format(
          new Date(new Date().getFullYear(), 0, 1),
          "yyyy-MM-dd"
        ),
        endDate: format(new Date(), "yyyy-MM-dd")
      }
    });
    setData([]);
  };

  const handleExport = async () => {
    setExportingCSV(true);
    const report = await actions.getCallDetailReport({
      filtered: filters.filtered
    });
    if (report) {
      downloadCSV(
        `CallDetail-${formatDate(new Date(), "yyyyMMdd")}`,
        report.calls.map((item) => ({
          ...item,
          billing_description: item.billing_description
            ? escapeCSVString(item.billing_description)
            : "",
          internal_notes: item.internal_notes
            ? escapeCSVString(item.internal_notes)
            : "",
          review_notes: item.review_notes
            ? escapeCSVString(item.review_notes)
            : ""
        })),
        CALL_DETAIL_COLUMNS
      );
    }
    setExportingCSV(false);
  };

  const handleExportPDF = () => {
    const queryFilters = {
      filtered: filters.filtered
    };
    const payload = {
      data: queryFilters,
      type: "CALL_DETAIL",
      format: "pdf"
    };
    actions.performExport(payload);
  };

  return (
    <div id="call-detail-page" className="report-page">
      <CallDetailFilters
        codes={codes}
        filters={filters}
        onChange={handleChange}
        onFetchClick={fetchData}
        onResetClick={handleResetClick}
        loading={loading}
        disabled={
          loading ||
          !Object.keys(filters).length ||
          !filters.filtered.startDate ||
          !filters.filtered.endDate
        }
        onCSVExport={handleExport}
        onPDFExport={handleExportPDF}
        exportDisabled={!data?.length || loading || exporting || exportingCSV}
      />
      <CallDetailList
        data={data}
        filtered={filters}
        loading={loading}
        count={count}
        onPageSizeChange={handlePageSizeChange}
        onPageChange={handlePageChange}
        page={filters.page}
        sorted={filters.sorted}
        pageSize={filters.pageSize}
        defaultPageSize={
          localStorage.getItem("DEFAULT_PAGE_SIZE_CALL_LIST")
            ? parseInt(localStorage.getItem("DEFAULT_PAGE_SIZE_CALL_LIST"))
            : 10
        }
      />
    </div>
  );
};

CallDetailPage.propTypes = {
  codes: PropTypes.array,
  actions: PropTypes.object,
  exporting: PropTypes.bool,
  exported: PropTypes.string
};

const mapStatesToProps = (state) => ({
  codes: state.reducers.codes,
  exporting: state.reducers.exporting,
  exported: state.reducers.exported
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      ...codeActions,
      ...reportActions,
      ...exportActions
    },
    dispatch
  )
});

export default connect(mapStatesToProps, mapDispatchToProps)(CallDetailPage);
